import { getIn } from 'icepick';
import {
  getSessionCategoryListOrders as getSessionCategoryListOrdersFromEvent,
  isAdmissionItemsEnabled as isAdmissionItemsEnabledFromEvent,
  isDiscountCodesEnabled as isDiscountCodesEnabledFromEvent,
  isFeesEnabled as isFeesEnabledFromEvent,
  isWaitlistEnabled as isWaitlistEnabledFromEvent,
  getOptionalSessions as getOptionalSessionsFromEvent
} from 'event-widgets/redux/selectors/event';
import { admissionItemIsVisible } from './shared';
import { getRegistrationPathIdForWidget }
  from 'event-widgets/redux/selectors/website/pluginData/registrationProcessNavigation';
import {
  getContactField as getContactFieldFromAppData,
  getContactFieldJsonPath as getContactFieldJsonPathFromAppData,
  getContactSubFieldJsonPath as getContactSubFieldJsonPathFromAppData,
  getRegistrationPath
} from 'event-widgets/redux/selectors/appData';
import { CLOSED } from 'event-widgets/clients/EventStatus';
import { values, filter } from 'lodash';
import { defaultMemoize, createSelector } from 'reselect';
import { isPasskeyEnabled as isPasskeyEnabledInEvent } from 'event-widgets/redux/selectors/eventTravel';

const EMPTY_OBJECT = Object.freeze({});

export const getEventId = (state) => {
  return getIn(state, ['event', 'id']);
};

export const getAdmissionItems = (state) => {
  return getIn(state, ['event', 'products', 'admissionItems']) || EMPTY_OBJECT;
};

export const getAdmissionItem = (state, admissionItemId) => {
  return getAdmissionItems(state)[admissionItemId];
};

export const getOptionalSessions = (state) => {
  return getOptionalSessionsFromEvent(state.event);
};

export const isFeesEnabled = (state) => {
  return isFeesEnabledFromEvent(state.event);
};

export const isDiscountCodesEnabled = (state) => {
  return isDiscountCodesEnabledFromEvent(state);
};

export const isAdmissionItemsEnabled = (state) => {
  return isAdmissionItemsEnabledFromEvent(state.event);
};

export const getSessionCategoryListOrders = (state) => {
  return getSessionCategoryListOrdersFromEvent(state.event);
};

export const isWaitlistEnabled = (state, registrationPathId) => {
  // Event level check first
  if (!isWaitlistEnabledFromEvent(state.event)) {
    return false;
  }
  const isRegistrationPathWaitistAllowed = getIn(state,
    ['appData', 'registrationSettings', 'registrationPaths', registrationPathId, 'allowWaitlist']);
  // The business rules at the registration path level are that:
  // 1. default is ON
  return isRegistrationPathWaitistAllowed === undefined || isRegistrationPathWaitistAllowed;
};

export const canWaitlist = (state, registrationPathId) =>
  CLOSED === state.event.status && isWaitlistEnabled(state, registrationPathId);

export const requireRegApproval = (state) => {
  const isRegistrationPathRegApprovalRequired = getIn(state,
    ['registrationForm', 'regCart', 'registrationApprovalRequired']);
  return !!isRegistrationPathRegApprovalRequired;
};

const sanitizedRegistrationTypeSettings = defaultMemoize(registrationTypeSettings => {
  return registrationTypeSettings && {
    ...registrationTypeSettings,
    limitVisibility: registrationTypeSettings.limitVisibility || false
  };
});

export function getRegistrationTypeSettings(state, registrationPathId) {
  return sanitizedRegistrationTypeSettings(state.appData
    .registrationSettings
    .registrationPaths[registrationPathId]
    .registrationTypeSettings);
}

export function getGuestRegistrationTypeSettings(state, registrationPathId) {
  return sanitizedRegistrationTypeSettings(state.appData
    .registrationSettings
    .registrationPaths[registrationPathId]
    .guestRegistrationSettings
    .registrationTypeSettings);
}

export function isGroupRegistrationEnabled(state, registrationPathId) {
  const hasGroupRegistrationFeatureEnabled = getIn(state.event,
    ['eventFeatureSetup', 'registrationProcess', 'groupRegistration']) || false;
  if (!hasGroupRegistrationFeatureEnabled) {
    return false;
  }
  const isGroupRegistrationAllowed = getIn(state,
    ['appData', 'registrationSettings', 'registrationPaths', registrationPathId,
      'allowsGroupRegistration']);
  return !!isGroupRegistrationAllowed;
}

/**
 * Determine which admission item should be selected by default for an invitee. This will only exist if there is exactly
 * one visible admission item for their reg type.
 */
export function getDefaultAdmissionItemIdSelectionForRegType(state, registrationTypeId) {
  const admissionItems = getAdmissionItems(state);
  const visibleItems = filter(admissionItems,
    admissionItem => admissionItemIsVisible(registrationTypeId, admissionItem));
  return visibleItems.length === 1 ? visibleItems[0].id : null;
}

export function getContactFieldJsonPath(state, widgetId, registrationFieldPageType,
  fieldId, ...subPaths) {
  const regPathId = getRegistrationPathIdForWidget(state, widgetId);
  return getContactFieldJsonPathFromAppData(regPathId, registrationFieldPageType, fieldId, ...subPaths);
}

export function getContactSubFieldJsonPath(state, widgetId, registrationFieldPageType,
  fieldId, ...subPaths) {
  const regPathId = getRegistrationPathIdForWidget(state, widgetId);
  return getContactSubFieldJsonPathFromAppData(regPathId, registrationFieldPageType, fieldId, ...subPaths);
}

export function getContactField(state, widgetId, registrationFieldPageType, fieldId) {
  const regPathId = getRegistrationPathIdForWidget(state, widgetId);
  return getContactFieldFromAppData(state.appData, regPathId, registrationFieldPageType, fieldId);
}

export function getSiteEditorRegistrationPath(state, registrationPathId) {
  return getRegistrationPath(state.appData, registrationPathId);
}

export function getSessionRegistrationRules(state) {
  return getIn(state, ['event', 'registrationSettings', 'sessionRegistrationRules']) || EMPTY_OBJECT;
}

export const getAdvancedSessionRules = createSelector(
  getSessionRegistrationRules,
  (sessionRegistrationRules) => {
    return filter(values(sessionRegistrationRules), rule => rule.isActive);
  }
);
export function isPasskeyEnabled(state) {
  return isPasskeyEnabledInEvent(getIn(state, ['eventTravel']));
}
